/*!
 * anglicism-engine.js — pure detection/scoring core for /anglicism-checker.
 *
 * Single source of truth shared by:
 *   - the browser UI (resources/views/tools/anglicism-checker.blade.php) via window.AnglicismEngine
 *   - the Node precision/recall test harness (tests/anglicism/*) via require()
 *
 * NO DOM, NO fetch, NO globals beyond the exported namespace. The blade keeps all
 * I/O (dict loading, rendering, exports); this file keeps tokenize/compile/analyze.
 *
 * v2 (etalon): context-aware homonym disambiguation, VDS-style necessity tier,
 * length-smoothed calibrated score, script-aware cue normalization, hardened
 * stemmer fallback. Output fields are a SUPERSET of v1 so renderers keep working.
 */
(function (root, factory) {
    'use strict';
    const api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (typeof window !== 'undefined') window.AnglicismEngine = api;
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    const ENGINE_VERSION = '2.0.0';

    /* ─────────── cue normalization (script-aware, stemmer-free) ───────────
     * Folds a token to a comparison form for homonym CUE matching only — never
     * used for the dictionary rx match itself (which stays literal so recall is
     * unchanged). Uses full Unicode normalize: NFD + strip combining marks (\p{M})
     * removes Latin accents (café→cafe), Arabic harakat/tashkil; NFC recomposes so
     * Hangul stays as syllables. Cheaper & more correct than the PHP manual tables
     * (JS has a real Unicode normalizer; ext-intl was unavailable in PHP). */
    function normalizeForCue(s) {
        s = String(s == null ? '' : s).toLowerCase().replace(/ё/g, 'е');
        try {
            s = s.normalize('NFD').replace(/\p{M}/gu, '').normalize('NFC');
        } catch (_) { /* very old engine: skip normalization */ }
        return s.replace(/ـ/g, ''); // Arabic tatweel (\p{Lm}, not \p{M})
    }

    /* ─────────── language detection ─────────── */
    function detectLangFromText(text) {
        const sample = text.slice(0, 4000);
        const score = { ru: 0, uk: 0, de: 0, fr: 0, es: 0, pt: 0, ko: 0, id: 0, ar: 0, en: 0 };
        // Cyrillic splits ru vs uk. Ukrainian text scored ru=50 with no uk bucket at all, so it
        // was routed to the Russian dictionary and missed uk-only anglicisms («мітинг»). Which of
        // the two it is — the site's one detector (timbrica-core: window.__cyrillicLang), not a
        // copy here: its function words and endings tell the uk phrase without і/ї/є/ґ, and a
        // «Київ» in a Russian text no longer tips it. (The words lists here were also matched
        // with \b, which JavaScript does not see between Cyrillic letters — they never fired.)
        if (/[а-яёіїєґ]/i.test(sample)) {
            score.ru += 40; score.uk += 40;
            const cl = typeof window !== 'undefined' && window ? window.__cyrillicLang : null;
            const cy = typeof cl === 'function' ? cl(sample) : 'ru';
            score[cy === 'uk' ? 'uk' : 'ru'] += 45;
        }
        if (/[가-힣]/.test(sample)) score.ko += 50;
        if (/[؀-ۿ]/.test(sample)) score.ar += 50;
        if (/[äöüß]/i.test(sample)) score.de += 20;
        if (/[àâçéèêëîïôùûüÿœ]/i.test(sample)) score.fr += 20;
        if (/[ñ¡¿]/i.test(sample)) score.es += 20;
        if (/[ãõâê]/i.test(sample)) score.pt += 15;
        const lower = ' ' + sample.toLowerCase() + ' ';
        if (/\b(der|die|das|und|nicht|ist|mit|für)\b/.test(lower)) score.de += 15;
        if (/\b(le|la|les|et|pour|dans|avec|est|une?)\b/.test(lower)) score.fr += 15;
        if (/\b(el|la|los|las|y|para|con|es|una?)\b/.test(lower)) score.es += 15;
        if (/\b(o|os|as|e|para|com|é|um|uma)\b/.test(lower)) score.pt += 10;
        if (/\b(yang|dan|untuk|dengan|adalah|tidak|ini|itu)\b/.test(lower)) score.id += 25;
        const enHits = (lower.match(/\b(the|and|of|to|in|is|that|for|it|on|with|as|this|are|was|were|be|by|an|or|not|but|have|has|from|which|will|can|their|they|them|its|at|if|than|then|when|while|so|do|does|did|been|being)\b/g) || []).length;
        if (enHits >= 4) score.en += 12 + Math.min(40, enHits);
        let best = null, max = 0;
        for (const k in score) { if (k !== 'en' && score[k] > max) { max = score[k]; best = k; } }
        return { lang: max > 10 ? best : null, en_score: score.en };
    }
    function looksEnglish(text) {
        const r = detectLangFromText(text);
        return r.lang === null && r.en_score >= 20;
    }

    /* ─────────── Russian stemmer (RU-only recall booster) ─────────── */
    const RUS_ENDINGS = [
        'иями', 'иях', 'ями', 'ами', 'ыми', 'ими', 'ого', 'его', 'ому', 'ему', 'их', 'ых',
        'ое', 'ее', 'ые', 'ие', 'ая', 'яя', 'ою', 'ею', 'ую', 'юю',
        'ом', 'ем', 'им', 'ым', 'ой', 'ей', 'ев', 'ов', 'ам', 'ям', 'ах', 'ях',
        'ый', 'ий', 'ия', 'ие', 'ью',
        'ы', 'и', 'а', 'я', 'е', 'у', 'ю', 'о',
    ].sort((a, b) => b.length - a.length);
    function stemRu(word) {
        if (!word) return word;
        word = word.toLowerCase().replace(/ё/g, 'е');
        if (word.length < 4) return word;
        let stem = word;
        for (const e of RUS_ENDINGS) {
            if (stem.endsWith(e) && stem.length - e.length >= 4) { stem = stem.slice(0, -e.length); break; }
        }
        if (stem.endsWith('н') && stem.length >= 5) stem = stem.slice(0, -1);
        return stem;
    }

    /* ─────────── tokenizer ─────────── */
    const NOISE_PATTERNS = [
        /https?:\/\/[^\s<>()]+/g,                        // URLs
        /\b[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}\b/gi,    // emails
        /(?:^|\s)[@#][\w.-]+/g,                            // @mentions, #hashtags
        /`[^`\n]{1,200}`/g,                                // inline code spans
        /\b[a-z]+(?:[A-Z][a-z]+)+\b/g,                     // camelCase identifier
        /\b[a-z]+(?:_[a-z]+)+\b/gi,                         // snake_case identifier
    ];
    function maskNoise(text) {
        let out = text;
        for (const re of NOISE_PATTERNS) out = out.replace(re, (m) => ' '.repeat(m.length));
        return out;
    }
    function tokenize(text) {
        const masked = maskNoise(text);
        const tokens = [];
        const re = /\p{L}[\p{L}\p{M}\p{Nd}'\-]*\p{L}|\p{L}/gu;
        let m;
        while ((m = re.exec(masked)) !== null) {
            tokens.push({ start: m.index, end: m.index + m[0].length, word: m[0] });
            if (m.index === re.lastIndex) re.lastIndex++;
        }
        return tokens;
    }
    function escapeRegex(s) { return s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'); }

    /* ─────────── necessity tier (VDS Anglizismenindex model) ───────────
     * needed   = no real native equivalent (terminology) — barely penalized.
     * nuance   = adds register/precision, assimilated — half weight.
     * avoidable= has a clean native replacement — full weight.
     * Stored on the entry when curated; otherwise DERIVED from legacy signals so
     * un-migrated entries still score coherently. Identical to the A4 migration. */
    const TIER_W = { needed: 0.15, nuance: 0.5, avoidable: 1.0 };
    function deriveTier(e) {
        const altStr = e.alt_strength || (Array.isArray(e.alt) && e.alt.length === 0 ? 'none' : 'strong');
        if (altStr === 'none' || (Array.isArray(e.alt) && e.alt.length === 0)) return 'needed';
        if (e.nat === true || (Array.isArray(e.ok) && e.ok.length > 0)) return 'nuance';
        return 'avoidable';
    }
    function effectiveTier(e) {
        return (e.tier === 'needed' || e.tier === 'nuance' || e.tier === 'avoidable') ? e.tier : deriveTier(e);
    }

    // ASCII \b / \B never assert correctly against Cyrillic / CJK / Arabic
    // tokens — JS word-boundary is ASCII-only even under the /u flag — and every
    // entry rx is matched ^…$ against an ALREADY-isolated token (see compileDict:
    // `new RegExp('^'+entryPat+'$')`, applied per-token), so a boundary anchor is
    // redundant for ASCII surfaces too. Stripping both is therefore a no-op for
    // the entries that currently work and a revival for the ~49 RU slang entries
    // that were silently dead (изи, флэкс, газлайт, стори, муд, тренди, агр…):
    // `^\bизи\b$` can never match because position-0→Cyrillic is not an ASCII
    // boundary. Safe because these dicts never use [\b] (backspace-in-class);
    // audited 2026-07-16. Found by tests/anglicism/coverage.cjs --self.
    function stripWordBoundaries(pat) {
        return String(pat).replace(/\\[bB]/g, '');
    }

    /* ─────────── dict compilation ─────────── */
    function compileDict(data) {
        const entries = [];
        const altParts = [];
        const phraseIndex = new Map();
        for (const e of (data.entries || [])) {
            if (typeof e.w === 'string' && /\s/.test(e.w.trim())) {
                const words = e.w.trim().toLowerCase().split(/\s+/).filter(Boolean);
                if (words.length >= 2) {
                    const head = words[0];
                    if (!phraseIndex.has(head)) phraseIndex.set(head, []);
                    phraseIndex.get(head).push({ entry: e, words });
                    continue;
                }
            }
            const parts = [];
            if (e.rx) parts.push(stripWordBoundaries(e.rx));
            if (Array.isArray(e.variants)) for (const v of e.variants) if (v) parts.push(escapeRegex(v));
            if (parts.length === 0) parts.push(escapeRegex(e.w));
            const entryPat = '(?:' + parts.join('|') + ')';
            let rx = null;
            try { rx = new RegExp('^' + entryPat + '$', 'iu'); }
            catch (_) { try { rx = new RegExp('^' + escapeRegex(e.w) + '$', 'iu'); } catch (__) { rx = null; } }
            if (!rx) continue;
            // Pre-compile homonym disambiguation data once, normalized for matching.
            let _hom = null;
            if (e.homonym) {
                _hom = {
                    def: e.default_sense === 'anglicism' ? 'anglicism' : 'native',
                    native: (e.native_cues || []).map(normalizeForCue).filter(c => c.length >= 3),
                    angl: (e.angl_cues || []).map(normalizeForCue).filter(c => c.length >= 3),
                    excuse: (e.excuse_rx || []).map(p => { try { return new RegExp(p, 'iu'); } catch (_) { return null; } }).filter(Boolean),
                };
            }
            // _altSafe = the alts safe to actually APPLY (one-click Replace +
            // clean-text). Excluded: (1) a MULTI-word alt whose first word this entry's
            // own rx re-matches is a RESTATEMENT — applying it duplicates the flagged word
            // ("облачные"→"облачные технологии" ⇒ "…технологии технологии") or injects a
            // parenthetical note as prose ("под"→"под (k8s)"); (2) an alt equal to the
            // flagged word itself (pure no-op). A single word that merely inflects under a
            // greedy rx (pt audit→auditoria) is a genuine replacement and stays.
            const _wLc = String(e.w || '').trim().toLowerCase();
            const _altSafe = (Array.isArray(e.alt) ? e.alt : []).filter(a => {
                const s = String(a).trim();
                if (!s) return false;
                if (s.toLowerCase() === _wLc) return false;         // no-op: alt == the word
                if (!/[\s ]/.test(s)) return true;                  // single word ≠ word → a real replacement
                const fw = (s.split(/[\s ]+/)[0] || '').toLowerCase();
                try { return !rx.test(fw); } catch (_) { return true; }  // multi-word restatement → drop
            });
            // soft_forms = inflected surfaces that are ALSO a different word and cannot be told
            // apart by spelling: «бага» is the genitive of «баг» and the river Бага; «матча» the
            // genitive of «матч» and the tea; «лида» the genitive of «лид» and the name Лида.
            // Hard-flagging them underlines the innocent word; suppressing them misses the real
            // anglicism. So these forms are demoted to the "possible" tier — a soft mark that
            // says "depends on context, you decide" and lets the reader make the call. The base
            // form («баг», «матч») is unaffected and stays a confident flag.
            const _soft = new Set((Array.isArray(e.soft_forms) ? e.soft_forms : [])
                .map(s => String(s).trim().toLowerCase()).filter(Boolean));
            entries.push(Object.assign({}, e, { _rx: rx, _tier: effectiveTier(e), _hom, _altSafe, _soft }));
            altParts.push(entryPat);
        }
        for (const arr of phraseIndex.values()) arr.sort((a, b) => b.words.length - a.words.length);
        let master = null;
        if (altParts.length > 0) { try { master = new RegExp('^(?:' + altParts.join('|') + ')$', 'iu'); } catch (_) { master = null; } }
        let stemMap = null;
        if (data.lang === 'ru') {
            stemMap = new Map();
            for (const e of entries) {
                // no_stem: opt out of the RU stem fallback. For short-stem entries
                // with a complete rx (домен, трофи, промо), the stemmer collapses a
                // DIFFERENT native word to the same 4-char stem (доме→домен,
                // трофей→трофи, промах→промо) → false positive. Their rx already
                // covers every real inflection, so they need no stem boost.
                if (e.no_stem) continue;
                const stem = stemRu(e.w);
                if (stem.length >= 4) { if (!stemMap.has(stem)) stemMap.set(stem, []); stemMap.get(stem).push(e); }
            }
        }
        return { lang: data.lang, version: data.version, entries, master, phraseIndex, stemMap, meta: data.meta || {} };
    }

    /* ─────────── built-in brand whitelist ─────────── */
    const BUILTIN_BRANDS = new Set([
        'apple', 'google', 'microsoft', 'amazon', 'meta', 'tesla', 'netflix', 'openai', 'anthropic', 'github', 'gitlab', 'slack',
        'telegram', 'whatsapp', 'discord', 'spotify', 'youtube', 'tiktok', 'instagram', 'twitter', 'facebook', 'linkedin', 'reddit',
        'iphone', 'android', 'windows', 'linux', 'macos', 'chrome', 'firefox', 'safari', 'edge', 'chatgpt', 'claude', 'gemini', 'copilot',
        'figma', 'notion', 'obsidian', 'docker', 'kubernetes', 'postgres', 'mysql', 'sqlite', 'redis', 'nginx', 'apache',
        'яндекс', 'сбер', 'сбербанк', 'wildberries', 'озон', 'ozon', 'авито', 'avito', 'тинькофф', 'tinkoff', 'альфа', 'втб', 'vtb',
        'мегафон', 'мтс', 'билайн', 'beeline', 'ростелеком', 'касперский', 'kaspersky', 'rambler', 'mail', 'почта', 'тиньков', 'skillbox', 'нетология',
        'kinopoisk', 'okko', 'ivi', 'rutube', 'дзен', 'vk', 'одноклассники', 'litres', 'bookmate', 'wb', 'yandex',
        'siemens', 'allianz', 'daimler', 'bmw', 'mercedes', 'audi', 'volkswagen', 'porsche', 'bosch', 'lufthansa', 'deutsche', 'klm', 'ikea', 'sap',
        'samsung', 'lg', 'kakao', 'naver', 'coupang', 'baemin', 'hyundai', 'kia',
        'mercadolibre', 'rappi', 'globant', 'bbva', 'telefonica', 'santander', 'inditex', 'zara',
        'ios', 'www', 'http', 'https', 'api', 'sdk', 'json', 'html', 'css', 'svg', 'pdf',
    ]);

    /* ─────────── raw-Latin anglicisms (untransliterated English) ───────────
     * The most blatant anglicism class: an English word written in Latin script
     * inside NON-Latin text ("Загрузите Sitemap", "дайте feedback"). The main
     * dictionary is native-script (Cyrillic/Hangul/Arabic), so these slip
     * through every rx. This is a CURATED whitelist — we flag only known
     * anglicisms, never "any Latin word" (that would fire on brands, quoted
     * English, code, proper nouns). Only fires for languages whose own script
     * is non-Latin (RU/KO/AR); Latin-script langs write these natively and the
     * main dict already covers them. Per-language native replacements; the
     * detector no-ops for a language until its alt array is populated. */
    const NONLATIN_LANGS = new Set(['ru', 'ko', 'ar', 'uk', 'be']);
    const RAW_NOTE = {
        ru: 'Английское слово латиницей без перевода — замените русским.',
        ko: '번역되지 않은 라틴 문자 영어 단어 — 한국어로 바꾸세요.',
        ar: 'كلمة إنجليزية بالحروف اللاتينية دون ترجمة — استبدلها بالعربية.',
    };
    // ru/ko/ar native replacements. ar = Modern Standard Arabic native terms
    // (Arabic strongly nativises). ko = native Korean where a clear native form
    // exists, else the standard Hangul rendering (still an improvement over raw
    // Latin). An entry fires for a language only when that language has an alt.
    const RAW_LATIN = {
        // digital / site UI
        sitemap:   { cat: 'general',  ru: ['карта сайта'], ko: ['사이트맵'], ar: ['خريطة الموقع'] },
        login:     { cat: 'general',  ru: ['вход'], ko: ['로그인'], ar: ['تسجيل الدخول'] },
        logout:    { cat: 'general',  ru: ['выход'], ko: ['로그아웃'], ar: ['تسجيل الخروج'] },
        signin:    { cat: 'general',  ru: ['вход'], ko: ['로그인'], ar: ['تسجيل الدخول'] },
        signup:    { cat: 'general',  ru: ['регистрация'], ko: ['회원가입'], ar: ['إنشاء حساب'] },
        download:  { cat: 'general',  ru: ['скачивание', 'загрузка'], ko: ['내려받기', '다운로드'], ar: ['تنزيل'] },
        upload:    { cat: 'general',  ru: ['загрузка', 'выгрузка'], ko: ['올리기', '업로드'], ar: ['رفع'] },
        home:      { cat: 'general',  ru: ['главная'], ko: ['홈', '처음'], ar: ['الرئيسية'] },
        about:     { cat: 'general',  ru: ['о нас', 'о сайте'], ko: ['소개'], ar: ['من نحن'] },
        contact:   { cat: 'general',  ru: ['контакты'], ko: ['연락처'], ar: ['اتصل بنا'] },
        contacts:  { cat: 'general',  ru: ['контакты'], ko: ['연락처'], ar: ['جهات الاتصال'] },
        search:    { cat: 'general',  ru: ['поиск'], ko: ['검색'], ar: ['بحث'] },
        support:   { cat: 'general',  ru: ['поддержка'], ko: ['지원'], ar: ['الدعم'] },
        help:      { cat: 'general',  ru: ['помощь'], ko: ['도움말'], ar: ['مساعدة'] },
        settings:  { cat: 'general',  ru: ['настройки'], ko: ['설정'], ar: ['الإعدادات'] },
        password:  { cat: 'general',  ru: ['пароль'], ko: ['비밀번호'], ar: ['كلمة المرور'] },
        username:  { cat: 'general',  ru: ['имя пользователя'], ko: ['사용자 이름'], ar: ['اسم المستخدم'] },
        // 'email' is by far the most naturalized of the set — flag it, but at
        // nuance tier / medium sev so it doesn't dominate an otherwise-clean page.
        email:     { cat: 'general',  ru: ['электронная почта', 'эл. почта'], ko: ['전자우편', '이메일'], ar: ['البريد الإلكتروني'], tier: 'nuance', sev: 'medium' },
        link:      { cat: 'general',  ru: ['ссылка'], ko: ['링크'], ar: ['رابط'] },
        account:   { cat: 'general',  ru: ['учётная запись'], ko: ['계정'], ar: ['حساب'] },
        // it / dev
        dashboard: { cat: 'it',       ru: ['панель управления'], ko: ['대시보드'], ar: ['لوحة التحكم'] },
        update:    { cat: 'it',       ru: ['обновление'], ko: ['갱신', '업데이트'], ar: ['تحديث'] },
        upgrade:   { cat: 'it',       ru: ['улучшение', 'обновление'], ko: ['업그레이드'], ar: ['ترقية'] },
        backup:    { cat: 'it',       ru: ['резервная копия'], ko: ['백업'], ar: ['نسخة احتياطية'] },
        setup:     { cat: 'it',       ru: ['настройка'], ko: ['설정'], ar: ['إعداد'] },
        roadmap:   { cat: 'it',       ru: ['план развития', 'дорожная карта'], ko: ['로드맵'], ar: ['خارطة الطريق'] },
        // business / work
        feedback:  { cat: 'business', ru: ['обратная связь', 'отзыв'], ko: ['의견', '피드백'], ar: ['ملاحظات', 'تعليقات'] },
        meeting:   { cat: 'business', ru: ['встреча', 'совещание'], ko: ['회의'], ar: ['اجتماع'] },
        deadline:  { cat: 'business', ru: ['срок'], ko: ['마감일'], ar: ['الموعد النهائي'] },
        briefing:  { cat: 'business', ru: ['инструктаж', 'планёрка'], ko: ['브리핑'], ar: ['إحاطة'] },
        workshop:  { cat: 'business', ru: ['мастер-класс'], ko: ['워크숍'], ar: ['ورشة عمل'] },
        deal:      { cat: 'business', ru: ['сделка'], ko: ['거래'], ar: ['صفقة'] },
        target:    { cat: 'business', ru: ['цель'], ko: ['목표'], ar: ['هدف'] },
        report:    { cat: 'business', ru: ['отчёт'], ko: ['보고서'], ar: ['تقرير'] },
        reporting: { cat: 'business', ru: ['отчётность'], ko: ['보고'], ar: ['إعداد التقارير'] },
        onboarding:{ cat: 'business', ru: ['адаптация'], ko: ['적응'], ar: ['التأهيل'] },
        team:      { cat: 'business', ru: ['команда'], ko: ['팀'], ar: ['فريق'] },
        // marketing / social
        newsletter:{ cat: 'marketing',ru: ['рассылка'], ko: ['소식지'], ar: ['النشرة الإخبارية'] },
        landing:   { cat: 'marketing',ru: ['посадочная страница'], ko: ['랜딩 페이지'], ar: ['صفحة الهبوط'] },
        subscribe: { cat: 'marketing',ru: ['подписаться'], ko: ['구독'], ar: ['اشترك'] },
        follower:  { cat: 'marketing',ru: ['подписчик'], ko: ['구독자'], ar: ['متابع'] },
        followers: { cat: 'marketing',ru: ['подписчики'], ko: ['구독자'], ar: ['متابعون'] },
        share:     { cat: 'marketing',ru: ['поделиться'], ko: ['공유'], ar: ['مشاركة'] },
        discount:  { cat: 'marketing',ru: ['скидка'], ko: ['할인'], ar: ['خصم'] },
        offer:     { cat: 'marketing',ru: ['предложение'], ko: ['혜택'], ar: ['عرض'] },
        review:    { cat: 'marketing',ru: ['отзыв'], ko: ['후기'], ar: ['مراجعة'] },
        reviews:   { cat: 'marketing',ru: ['отзывы'], ko: ['후기'], ar: ['مراجعات'] },
        // commerce / ui
        checkout:  { cat: 'business', ru: ['оформление заказа'], ko: ['결제'], ar: ['إتمام الشراء'] },
        cart:      { cat: 'business', ru: ['корзина'], ko: ['장바구니'], ar: ['سلة التسوق'] },
        order:     { cat: 'business', ru: ['заказ'], ko: ['주문'], ar: ['طلب'] },
        shipping:  { cat: 'business', ru: ['доставка'], ko: ['배송'], ar: ['الشحن'] },
        delivery:  { cat: 'business', ru: ['доставка'], ko: ['배송'], ar: ['التوصيل'] },
        payment:   { cat: 'business', ru: ['оплата'], ko: ['결제'], ar: ['الدفع'] },
        pricing:   { cat: 'business', ru: ['цены', 'тарифы'], ko: ['가격'], ar: ['الأسعار'] },
        price:     { cat: 'business', ru: ['цена'], ko: ['가격'], ar: ['السعر'] },
        product:   { cat: 'business', ru: ['товар'], ko: ['제품'], ar: ['منتج'] },
        products:  { cat: 'business', ru: ['товары'], ko: ['제품'], ar: ['منتجات'] },
        service:   { cat: 'business', ru: ['услуга'], ko: ['서비스'], ar: ['خدمة'] },
        services:  { cat: 'business', ru: ['услуги'], ko: ['서비스'], ar: ['خدمات'] },
        store:     { cat: 'business', ru: ['магазин'], ko: ['상점'], ar: ['متجر'] },
        // lifestyle / general
        lifestyle: { cat: 'casual',   ru: ['образ жизни'], ko: ['생활 방식'], ar: ['نمط الحياة'] },
        weekend:   { cat: 'casual',   ru: ['выходные'], ko: ['주말'], ar: ['عطلة نهاية الأسبوع'] },
        event:     { cat: 'casual',   ru: ['событие', 'мероприятие'], ko: ['행사'], ar: ['فعالية'] },
        events:    { cat: 'casual',   ru: ['события', 'мероприятия'], ko: ['행사'], ar: ['فعاليات'] },
    };

    function buildBrandSet(dict) {
        if (!dict.meta || !Array.isArray(dict.meta.brands)) return BUILTIN_BRANDS;
        const out = new Set(BUILTIN_BRANDS);
        for (const b of dict.meta.brands) if (b) out.add(String(b).toLowerCase());
        return out;
    }

    /* ─────────── homonym disambiguation ───────────
     * Returns { action:'flag'|'possible'|'suppress', why }.
     * Scans a ±WINDOW token context (cue-normalized) for native vs anglicism cues.
     *   anglicism cue & no native cue → flag (full weight)
     *   native cue & no anglicism cue → suppress (it's the native sense)
     *   both cues                     → possible (ambiguous, low weight, "depends on context")
     *   neither                       → fall back to default_sense
     * Phrase-level excuse_rx (e.g. "великий пост") suppress outright. */
    const CTX_WINDOW = 6;
    function disambiguate(hom, tokens, idx, text, start, end) {
        if (!hom) return { action: 'flag', why: 'dict' };
        // phrase excuse: test a small text slice around the hit
        if (hom.excuse && hom.excuse.length) {
            const sliceStart = Math.max(0, start - 40), sliceEnd = Math.min(text.length, end + 40);
            const slice = text.slice(sliceStart, sliceEnd);
            for (const rx of hom.excuse) { rx.lastIndex = 0; if (rx.test(slice)) return { action: 'suppress', why: 'excuse_phrase' }; }
        }
        let nat = false, ang = false;
        const lo = Math.max(0, idx - CTX_WINDOW), hi = Math.min(tokens.length - 1, idx + CTX_WINDOW);
        // The disambiguating context is the hit's OWN sentence. A ±6-token window blindly reaches
        // across a sentence boundary, so «Белая лайка … упряжки. Поставь лайк под фото» let the
        // social-media cues of the SECOND sentence vote on the dog in the FIRST — both cues fired,
        // the verdict fell to `possible`, and the breed was underlined on prod even though the
        // sentence alone was clean. A sentence terminator (.!?…) in the gap between two adjacent
        // tokens ends the window on that side.
        const crossesBoundary = (a, b) => /[.!?…]/.test(text.slice(tokens[a].end, tokens[b].start));
        for (let j = lo; j <= hi; j++) {
            if (j === idx) continue;
            // Walk outward from the hit; stop as soon as a sentence break sits between steps.
            const step = j < idx ? 1 : -1;
            let broke = false;
            for (let k = j; k !== idx; k += step) {
                const [a, b] = step > 0 ? [k, k + 1] : [k - 1, k];
                if (crossesBoundary(a, b)) { broke = true; break; }
            }
            if (broke) continue;
            const ctx = normalizeForCue(tokens[j].word);
            if (ctx.length < 3) continue;
            // Forward match handles inflected context («messages» starts with the cue «messag»).
            // The reverse match is for a context word SHORTER than its cue, and it needs a length
            // floor on the CONTEXT token too — not just the cue. Without it the French possessive
            // «mes» is a prefix of «messag», so «mes chats dorment» read as chat-window context and
            // the tool underlined someone's cats. Four characters is short enough to keep genuine
            // truncations and long enough to exclude function words (mes, ses, nos, les, bot…).
            const revOk = ctx.length >= 4;
            if (!nat) for (const c of hom.native) { if (ctx.startsWith(c) || (revOk && c.length >= 4 && c.startsWith(ctx))) { nat = true; break; } }
            if (!ang) for (const c of hom.angl) { if (ctx.startsWith(c) || (revOk && c.length >= 4 && c.startsWith(ctx))) { ang = true; break; } }
            if (nat && ang) break;
        }
        if (ang && !nat) return { action: 'flag', why: 'angl_context' };
        if (nat && !ang) return { action: 'suppress', why: 'native_context' };
        if (nat && ang) return { action: 'possible', why: 'ambiguous_context' };
        return hom.def === 'native'
            ? { action: 'suppress', why: 'default_native' }
            : { action: 'flag', why: 'default_anglicism' };
    }

    /* ─────────── score model (length-smoothed, tier-weighted) ─────────── */
    const SEV_W = { low: 0.5, medium: 1.0, high: 2.0 };
    const STRICT_MUL = { lax: 0.6, balanced: 1.0, strict: 1.6, purist: 2.4 };
    const SMOOTH_K = 60;   // pseudo-words: pulls short texts toward 100
    const PEN_C = 0.04;    // density midpoint of the diminishing-returns curve
    const DISAMBIG_MUL = { flag: 1.0, possible: 0.3, suppress: 0 };
    function scoreFromWeighted(weighted, totalWords) {
        const rate = totalWords > 0 ? weighted / (totalWords + SMOOTH_K) : 0;
        const penalty = 100 * (rate / (rate + PEN_C));
        const score = Math.max(0, Math.round(100 - penalty));
        const grade = score >= 90 ? 'A' : score >= 75 ? 'B' : score >= 60 ? 'C' : score >= 40 ? 'D' : 'F';
        return { score, grade, density: +(totalWords > 0 ? (weighted / totalWords) * 1000 : 0).toFixed(2) };
    }

    /* ─────────── analyser (pure) ─────────── */
    function analyze(text, dict, opts) {
        const settings = Object.assign({
            strictness: 'balanced', domain: 'general',
            allowNaturalized: true, allowTechnical: true,
            whitelist: new Set(), region: 'auto',
        }, opts);
        const brandSet = buildBrandSet(dict);
        const strictMul = STRICT_MUL[settings.strictness] || 1.0;
        // raw-Latin detection: only for non-Latin-script target languages
        const rawLatinOn = NONLATIN_LANGS.has(dict.lang);

        const tokens = tokenize(text);
        const hits = [];
        const consumed = new Uint8Array(tokens.length);
        let weighted = 0;

        // Shared scoring for phrase + single-token paths.
        const scoreEntry = (entry, surface, start, end, tokIdx) => {
            const lc = entry.w.toLowerCase();
            if (settings.whitelist.has(lc)) return null;

            // Homonym disambiguation (only entries carrying _hom).
            const dis = entry._hom ? disambiguate(entry._hom, tokens, tokIdx, text, start, end) : { action: 'flag', why: 'dict' };
            if (dis.action === 'suppress') return null;
            // An ambiguous inflected surface («бага», «матча», «лида») is never a confident flag —
            // it drops to "possible" so the reader decides. A cue-driven suppress still wins (the
            // dog sentence stays clean); we only soften what would otherwise be a hard flag.
            if (dis.action === 'flag' && entry._soft && entry._soft.size) {
                const sfLc = String(surface || '').toLowerCase();
                // Exact match, or a prefix STRICTLY LONGER than the base word — «доменн» softens
                // «доменная/доменные» (the furnace) while «домен/домена» (the anglicism) is left
                // alone, because the base can only ever match a soft token by exact equality and
                // the base itself is never listed.
                let soft = false;
                for (const t of entry._soft) {
                    if (sfLc === t || (t.length > lc.length && sfLc.startsWith(t))) { soft = true; break; }
                }
                if (soft) { dis.action = 'possible'; dis.why = 'ambiguous_form'; }
            }
            const disMul = DISAMBIG_MUL[dis.action];

            const domOk = (entry.ok || []).includes(settings.domain);
            const techOk = settings.allowTechnical && (entry.cat === 'it');
            const isDomainAcceptable = domOk || techOk;
            if (isDomainAcceptable && settings.strictness === 'lax') return null;

            const tier = entry._tier || effectiveTier(entry);
            // purist ignores the naturalisation discount baked into 'nuance'/'needed'
            const tierW = settings.strictness === 'purist' ? Math.max(TIER_W[tier], 0.6) : TIER_W[tier];
            const baseW = SEV_W[entry.sev] || 1.0;
            const domainFactor = isDomainAcceptable ? 0.4 : 1.0;
            const region = settings.region;
            let regionFactor = 1.0;
            if (region && region !== 'auto' && entry.region && entry.region !== region) regionFactor = 0.3;

            const localW = baseW * tierW * domainFactor * regionFactor * disMul * strictMul;

            const confidence = Math.round(100 * Math.min(1,
                disMul * (isDomainAcceptable ? 0.55 : 1) * (tier === 'needed' ? 0.5 : tier === 'nuance' ? 0.8 : 1) * regionFactor));

            return {
                hit: {
                    entry, start, end, surface,
                    severity: entry.sev,
                    tier,
                    disambig: dis.action,        // 'flag' | 'possible'
                    why: dis.why,                // reason code (UI explains)
                    confidence,
                    ignored: false,
                    domainAcceptable: isDomainAcceptable,
                    regionMismatch: regionFactor < 1.0,
                    pseudo: !!entry.pseudo,
                    raw: !!entry.raw,
                },
                weight: localW,
            };
        };

        // Pass 1: multi-word phrases (longest-match, no overlap).
        if (dict.phraseIndex && dict.phraseIndex.size > 0) {
            for (let i = 0; i < tokens.length; i++) {
                if (consumed[i]) continue;
                const candidates = dict.phraseIndex.get(tokens[i].word.toLowerCase());
                if (!candidates) continue;
                for (const c of candidates) {
                    const need = c.words.length;
                    if (i + need > tokens.length) continue;
                    let ok = true;
                    for (let j = 1; j < need; j++) if (tokens[i + j].word.toLowerCase() !== c.words[j]) { ok = false; break; }
                    if (!ok) continue;
                    let contiguous = true;
                    for (let j = 0; j < need - 1; j++) if (tokens[i + j + 1].start - tokens[i + j].end > 3) { contiguous = false; break; }
                    if (!contiguous) continue;
                    const phraseStart = tokens[i].start, phraseEnd = tokens[i + need - 1].end;
                    const r = scoreEntry(c.entry, text.slice(phraseStart, phraseEnd), phraseStart, phraseEnd, i);
                    if (r) { weighted += r.weight; hits.push(r.hit); for (let j = 0; j < need; j++) consumed[i + j] = 1; }
                    break;
                }
            }
        }

        // Pass 2: single-token entries.
        for (let i = 0; i < tokens.length; i++) {
            if (consumed[i]) continue;
            const tok = tokens[i];
            const lc = tok.word.toLowerCase();
            if (settings.whitelist.has(lc)) continue;
            if (brandSet.has(lc)) continue;

            let matched = null;
            if (dict.master && dict.master.test(tok.word)) {
                for (const e of dict.entries) { if (e._rx.test(tok.word)) { matched = e; break; } }
            }
            // RU stem fallback — hardened: ≥4-char common prefix (was 3), so the
            // 'чёрного'→'чёрн'(churn) class of collisions cannot resurface even if a
            // bad rx slips the lint.
            if (!matched && dict.stemMap && tok.word.length >= 4 && /[а-яё]/i.test(tok.word)) {
                const tokenStem = stemRu(tok.word);
                if (tokenStem.length >= 4) {
                    const candidates = dict.stemMap.get(tokenStem);
                    if (candidates) {
                        const tokHead = lc.slice(0, 4);
                        for (const e of candidates) { if (e.w.toLowerCase().slice(0, 4) === tokHead) { matched = e; break; } }
                    }
                }
            }
            // raw-Latin: an untransliterated English anglicism in non-Latin text.
            // Curated list only; guard against short all-caps acronyms (API, SEO)
            // and require ≥4 pure-Latin letters so brands/code slip past.
            if (!matched && rawLatinOn) {
                // Match the LEADING Latin run, not the whole token: Korean
                // particles glue to the word ("feedback을" → feedback), and a
                // native-script case-ending occasionally does too. Strip the
                // native tail before the lookup.
                const lm = tok.word.match(/^[a-z][a-z'’-]*/i);
                const latin = lm ? lm[0] : '';
                const raw = RAW_LATIN[latin.toLowerCase()];
                const pEnd = tok.start + latin.length;
                // Glued to a "." + ext / another segment, or preceded by . / @ →
                // filename (sitemap.xml), domain (store.example.com) or path. Skip.
                const prevCh = tok.start > 0 ? text[tok.start - 1] : '';
                const nextCh = text[pEnd] || '';
                const inIdentifier = (nextCh === '.' && /[a-z0-9]/i.test(text[pEnd + 1] || ''))
                    || prevCh === '.' || prevCh === '/' || prevCh === '@' || prevCh === '\\';
                // A Latin word flanked by another Latin word is a TITLE / name /
                // quote ("Quake III: Team Arena", "Child Support", "Lookout
                // Weekend"), not a lone anglicism embedded in native text ("наш
                // support ответит"). Skip when a Latin neighbour is present.
                const prevW = i > 0 ? tokens[i - 1].word : '';
                const nextW = i + 1 < tokens.length ? tokens[i + 1].word : '';
                const inLatinRun = /^[a-z]{2,}/i.test(prevW) || /^[a-z]{2,}/i.test(nextW);
                if (raw && raw[dict.lang] && !inIdentifier && !inLatinRun
                    && latin.length >= 4
                    && !(latin === latin.toUpperCase() && latin.length <= 5)) {
                    const rawEntry = {
                        w: latin.toLowerCase(), alt: raw[dict.lang], sev: raw.sev || 'high',
                        cat: raw.cat || 'general', nat: raw.tier === 'nuance', ok: [],
                        _tier: raw.tier || 'avoidable', raw: true,
                        note: RAW_NOTE[dict.lang] || RAW_NOTE.ru,
                    };
                    const rr = scoreEntry(rawEntry, latin, tok.start, pEnd, i);
                    if (rr) { weighted += rr.weight; hits.push(rr.hit); }
                    continue;
                }
            }
            // Hyphenated compound (German Fake-News / Social-Media-Manager, or
            // Russian PR-менеджер): the tokenizer keeps it as ONE token so the
            // whole-token match missed. Split on hyphens and flag any anglicism
            // PARTS at their real offsets. Parts <3 chars (acronyms PR/IT) skip.
            if (!matched && tok.word.indexOf('-') > 0) {
                let off = 0;
                for (const part of tok.word.split('-')) {
                    const pStart = tok.start + off;
                    off += part.length + 1;
                    if (part.length < 3) continue;
                    const plc = part.toLowerCase();
                    if (brandSet.has(plc) || settings.whitelist.has(plc)) continue;
                    if (dict.master && dict.master.test(part)) {
                        let pe = null;
                        for (const e of dict.entries) { if (e._rx.test(part)) { pe = e; break; } }
                        if (pe) {
                            const r = scoreEntry(pe, part, pStart, pStart + part.length, i);
                            if (r) { weighted += r.weight; hits.push(r.hit); }
                        }
                    }
                }
                continue;
            }
            if (!matched) continue;
            const r = scoreEntry(matched, tok.word, tok.start, tok.end, i);
            if (!r) continue;
            weighted += r.weight; hits.push(r.hit);
        }

        const totalWords = tokens.length;
        const { score, grade, density } = scoreFromWeighted(weighted, totalWords);

        // stats
        const counts = {};
        const cats = {
            it: { low: 0, med: 0, high: 0 }, business: { low: 0, med: 0, high: 0 }, marketing: { low: 0, med: 0, high: 0 },
            fashion: { low: 0, med: 0, high: 0 }, casual: { low: 0, med: 0, high: 0 }, sports: { low: 0, med: 0, high: 0 }, general: { low: 0, med: 0, high: 0 },
        };
        for (const h of hits) {
            const lc = h.entry.w.toLowerCase();
            counts[lc] = (counts[lc] || 0) + 1;
            const sevK = h.severity === 'medium' ? 'med' : h.severity;
            if (cats[h.entry.cat]) cats[h.entry.cat][sevK]++;
        }
        let topWord = '', topCount = 0;
        for (const k in counts) { if (counts[k] > topCount) { topCount = counts[k]; topWord = k; } }

        return { score, grade, density, totalWords, hits, topWord, topCount, cats, settings };
    }

    /* ─────────── all-in-one secondary axes (subsume Главред + text.ru + Свежий взгляд) ───────────
     * Anglicism purity stays the cited HEADLINE. These three are SECONDARY, shown as
     * descriptive bands ('good'/'ok'/'high'), NOT numbers that feed the headline —
     * heuristic signals must not poison the dictionary-backed score. Водность is
     * typologically incomparable across languages, so its bands come from per-language
     * thresholds in the style data and are labelled "relative to typical {lang} prose". */
    const FN_RATIO_DEFAULT = [0.42, 0.58];
    function band3(v, lo, hi) { return v < lo ? 'low' : v > hi ? 'high' : 'typical'; }

    function analyzeStyle(text, style) {
        if (!style) return null;
        const tokens = tokenize(text);
        const N = tokens.length;
        const empty = { words: N,
            kancelyarit: { count: 0, per100: 0, band: 'low', hits: [] },
            vodnost: { ratio: 0, band: 'typical', count: 0 },
            repeats: { count: 0, per1000: 0, band: 'low', words: [] } };
        if (!N) return empty;
        const norm = tokens.map(t => normalizeForCue(t.word));

        // — Канцелярит / clichés (bureaucratese): prefix-match single stems + phrase regexes —
        const kSingles = (style.kancelyarit_words || []).map(normalizeForCue).filter(w => w.length >= 3);
        const kHits = [];
        for (let i = 0; i < N; i++) {
            for (const k of kSingles) { if (norm[i].startsWith(k)) { kHits.push(tokens[i].word.toLowerCase()); break; } }
        }
        const lcText = text.toLowerCase();
        for (const ph of (style.kancelyarit_phrases || [])) {
            let rx; try { rx = new RegExp(ph, 'giu'); } catch (_) { continue; }
            const m = lcText.match(rx);
            if (m) for (const x of m) kHits.push(x.trim());
        }
        const kPer100 = (kHits.length / N) * 100;

        // — Водность (function-word + filler share) —
        const fnSet = new Set((style.function_words || []).map(normalizeForCue));
        const fillerSingles = (style.filler || []).map(normalizeForCue).filter(w => w.length >= 2);
        let fnCount = 0;
        for (let i = 0; i < N; i++) {
            if (fnSet.has(norm[i])) { fnCount++; continue; }
            for (const f of fillerSingles) { if (norm[i].startsWith(f)) { fnCount++; break; } }
        }
        const ratio = fnCount / N;
        const bands = Array.isArray(style.fn_ratio_bands) && style.fn_ratio_bands.length === 2 ? style.fn_ratio_bands : FN_RATIO_DEFAULT;

        // — Повторы (close content-word repetition; RU stem-aware to catch inflections) —
        const WIN = 50;
        const lastPos = new Map();
        let repCount = 0; const repWords = [];
        for (let i = 0; i < N; i++) {
            const w = norm[i];
            if (w.length < 5 || fnSet.has(w)) continue;
            const key = style.lang === 'ru' ? stemRu(tokens[i].word) : w;
            if (key.length < 4) continue;
            if (lastPos.has(key) && (i - lastPos.get(key)) <= WIN) { repCount++; if (repWords.length < 30) repWords.push(tokens[i].word.toLowerCase()); }
            lastPos.set(key, i);
        }
        const repPer1000 = (repCount / N) * 1000;

        return {
            words: N,
            kancelyarit: { count: kHits.length, per100: +kPer100.toFixed(2), band: band3(kPer100, 1, 3), hits: [...new Set(kHits)].slice(0, 40) },
            vodnost: { ratio: +ratio.toFixed(3), band: band3(ratio, bands[0], bands[1]), count: fnCount },
            repeats: { count: repCount, per1000: +repPer1000.toFixed(1), band: band3(repPer1000, 8, 20), words: [...new Set(repWords)].slice(0, 30) },
        };
    }

    return {
        ENGINE_VERSION,
        analyzeStyle,
        normalizeForCue,
        detectLangFromText, looksEnglish,
        stemRu, tokenize, maskNoise, escapeRegex,
        compileDict, buildBrandSet, BUILTIN_BRANDS,
        deriveTier, effectiveTier,
        disambiguate, analyze, scoreFromWeighted,
    };
});
